import React from 'react';
import { View, Pressable, Text, StyleSheet, Platform } from 'react-native';
import { X } from '../icons';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';

/** Matches {@link FormDropdown} closed multiselect chip tokens (`webMultiChip` / `webMultiChipLabel`). */
export type MemberPickerChipProps = {
  label: string;
  onRemove?: () => void;
  disabled?: boolean;
  testID?: string;
};

export const MemberPickerChip: React.FC<MemberPickerChipProps> = ({
  label,
  onRemove,
  disabled = false,
  testID,
}) => {
  const removable = Boolean(onRemove) && !disabled;
  return (
    <Pressable
      testID={testID}
      disabled={!removable}
      onPress={removable ? onRemove : undefined}
      style={({ pressed }) => [
        styles.chip,
        pressed && removable && styles.chipPressed,
      ]}
      accessibilityLabel={removable ? `Remove ${label}` : label}
      accessibilityRole={removable ? 'button' : 'text'}
      hitSlop={spacing.xs}
    >
      <Text style={styles.chipLabel} numberOfLines={1}>
        {label}
      </Text>
      {removable ? (
        <View style={styles.chipIconMargin}>
          <X size={12} color={colors.textSecondary} />
        </View>
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    maxWidth: 280,
    paddingVertical: spacing.formMultiSelectChipPaddingVertical,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.surfaceMultiSelectChip,
    borderWidth: 1,
    borderColor: colors.borderMultiSelectChip,
    ...Platform.select({
      web: {
        gap: spacing.xs,
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  chipPressed: {
    opacity: 0.85,
  },
  chipIconMargin: {
    marginLeft: spacing.xs,
  },
  chipLabel: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    fontWeight: '400',
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
      },
      default: {},
    }),
  },
});
